import type { RevenueReport } from '@witmetrics/utils';
import { sortNumerical } from '@witmetrics/utils/arrays';
import { theme } from '@/utils/theme';
import { exportArraysToSpreadsheet } from '@/utils/spreadsheets';
import { buildExportTimestamp } from '@/utils/time';
import type { LeadsPerformance, ReportByMonth } from './types';
import { BLANK_MONTH } from './constants';

export const classes = {
  wrapper: 'flex justify-between max-w-[800px] px-6 py-8',
  cell: 'py-2 transition-colors text-grey-8 font-medium',
  cellUp: 'wm-cell-up bg-green-1',
  cellDown: 'wm-cell-down bg-red-1',
  stickyCell: 'py-2 sticky left-0 bg-white border-r border-grey-3',
  summaryCell: 'py-2 font-semibold',
  loadingMessage:
    'h-full w-full place-center text-2xl font-light text-grey-6 p-8',
};

export const styles = {
  tableBody: {
    '& .MuiTableRow-root:last-child td.sticky': {
      borderRight: `1px solid ${theme.palette['grey-3'].main}`,
    },
    // Hovering over any part of the row
    ...perRow(theme.palette['grey-2'].main),
    ...perRow(theme.palette['green-2'].main, '.wm-cell-up'),
    ...perRow(theme.palette['red-2'].main, '.wm-cell-down'),
    // Column hovering
    ...perCell(theme.palette['grey-2'].main),
    ...perCell(theme.palette['green-2'].main, '.wm-cell-up'),
    ...perCell(theme.palette['red-2'].main, '.wm-cell-down'),
  },
};

function perRow(color: string, subClass = '') {
  return {
    [`& .MuiTableRow-hover:hover ${subClass}`]: {
      backgroundColor: color,
      color: theme.palette['grey-9'].main,
    },
  };
}

function perCell(color: string, subClass = '') {
  return [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14].reduce(
    (a, b) => ({
      ...a,
      [`&:has(tr > *:nth-of-type(${b}):hover) tr > *:nth-of-type(${b})${subClass}`]:
        {
          backgroundColor: color,
          color: theme.palette['grey-9'].main,
        },
    }),
    {}
  );
}

export function groupByMonth(report: RevenueReport): LeadsPerformance[] {
  let result: ReportByMonth = new Map();
  report.data.forEach(({ startDate, leads }) => {
    const month = new Date(startDate).getMonth();
    if (!result.has(month)) {
      result.set(month, leads);
    } else {
      let updates = { ...result.get(month)! };
      updates.treatmentOutstanding += leads.treatmentOutstanding;
      updates.treatmentAccepted += leads.treatmentAccepted;
      updates.revenue.value += leads.revenue.value;
      result.set(month, updates);
    }
  });
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].forEach((month) => {
    if (!result.has(month)) result.set(month, { ...BLANK_MONTH });
  });
  return sortNumerical(Array.from(result.keys())).map(
    (key) => result.get(key)!
  );
}

export function compareByMonth(
  report1: RevenueReport,
  report2?: RevenueReport
) {
  const result1 = groupByMonth(report1);
  if (!report2) {
    return result1.map((data) => ({
      treatmentOutstanding: null,
      treatmentAccepted: null,
      revenue: {
        currency: data.revenue.currency,
        value: null,
      },
    }));
  }
  const result2 = groupByMonth(report2);
  return result1.map((data, index) => {
    const compareData = result2[index];
    return {
      treatmentOutstanding:
        data.treatmentOutstanding - compareData.treatmentOutstanding,
      treatmentAccepted: data.treatmentAccepted - compareData.treatmentAccepted,
      revenue: {
        currency: data.revenue.currency,
        value: data.revenue.value - compareData.revenue.value,
      },
    };
  });
}

export function sumReportValue(
  report: LeadsPerformance[],
  key: keyof LeadsPerformance
) {
  return key === 'revenue'
    ? report.reduce((acc, curr) => acc + curr[key].value, 0)
    : report.reduce((acc, curr) => acc + curr[key], 0);
}

export function exportReport(
  currentReport: RevenueReport | null,
  previousReport: RevenueReport | null
) {
  if (!currentReport || !previousReport) return null;
  const currentData = groupByMonth(currentReport);
  const previousData = groupByMonth(previousReport);
  return exportArraysToSpreadsheet({
    data: [
      buildTitleRow(2024),
      buildExportRow('Patients (Accepted)', currentData, 'treatmentAccepted'),
      buildExportRow('Revenue', currentData, 'revenue'),
      {},
      buildTitleRow(2023),
      buildExportRow('Patients (Accepted)', previousData, 'treatmentAccepted'),
      buildExportRow('Revenue', previousData, 'revenue'),
    ],
    sheetName: 'Sales Overview',
    filename: `witmetrics-sales_overview_${buildExportTimestamp()}`,
  });
}

function buildTitleRow(year: number) {
  return [
    `${year}`,
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
    'Total',
  ];
}

function buildExportRow(
  label: string,
  rowData: LeadsPerformance[],
  key: keyof LeadsPerformance
) {
  return [
    label,
    ...rowData.map((data) => formatColumnValue(data, key)),
    sumReportValue(rowData, 'treatmentAccepted'),
  ];
}

function formatColumnValue(
  value: LeadsPerformance,
  key: keyof LeadsPerformance
) {
  return key === 'revenue' ? value[key].value : value[key];
}
